import { dataUrlToBlob } from "lib/actions/views/dataUrlToBlob";
import { patchView } from "lib/actions/views/patchView";
import { betterJsonPatchCompare } from "lib/actions/util/betterJsonPatchCompare";
import { useViewsSnapshotUploads } from "lib/contexts/GlobalContexts/ViewsSnapshotUploadContext";
import { ViewModel } from "lib/types/views/ViewModel";
import { AnnotationState } from "@markerjs/markerjs3";
import { v4 as uuid } from "uuid";

type AnnotationSaveResult = {
  snapshotAnnotationJson: object | null;
  annotatedSnapshotId: string | null;
};

/**
 * Returns a save function that uploads the annotated snapshot, patches
 * the view via API, and resolves with the updated annotation fields. Saving no
 * markers clears both the annotations and the annotated snapshot.
 */
export function usePatchViewAnnotation(
  projectId: number,
  view: ViewModel | null | undefined,
) {
  const { addAnnotatedSnapshot } = useViewsSnapshotUploads();

  return async function saveAnnotation(
    annotationState: AnnotationState | null,
    annotatedSnapshotDataUrl?: string,
  ): Promise<AnnotationSaveResult> {
    if (!view?.snapshotId) return;

    const hasMarkers = !!annotationState?.markers.length;
    const newAnnotationState = hasMarkers ? annotationState : null;
    const newAnnotatedSnapshotId = hasMarkers ? uuid().substring(0, 8) : null;

    if (hasMarkers) {
      await addAnnotatedSnapshot(
        projectId,
        view.snapshotId,
        newAnnotatedSnapshotId,
        await dataUrlToBlob(annotatedSnapshotDataUrl),
      );
    }

    const patches = betterJsonPatchCompare<ViewModel>(
      view,
      {
        ...view,
        snapshotAnnotationJson: newAnnotationState,
        annotatedSnapshotId: newAnnotatedSnapshotId,
      },
      ["snapshotAnnotationJson"],
    );
    if (patches.length) {
      await patchView(projectId, view.id, patches);
    }

    return {
      snapshotAnnotationJson: newAnnotationState,
      annotatedSnapshotId: newAnnotatedSnapshotId,
    };
  };
}
